import React, { useEffect, useState } from "react";
import { DialogContent } from "@mui/material";
import { OrderModel } from "../../../../../shared/models/order/order-item.model";
import Message from "../../../../../components/message";
import { MessageSections } from "../../../../../components/message/message";
import { UserAddress } from "../../../../../shared/models/user/user-address";
import { WarehouseModel } from "../../../../../shared/models/order/warehouse.model";
import { useAppDispatch, useAppSelector } from "../../../../../../redux/store";
import {
  changePagination,
  getMyAddresses,
} from "../../../../../../redux/slices/user-addresses.slice";
import { SingleSelectItem } from "../../../../../components/single-select-item/single-select-item";
import { BarekamIcon } from "../../../../../components/icon/barekam-icon";
import BarekamText from "../../../../../components/text";
import { OrderIcons } from "../../../../../components/icon/variants";
import BarekamButton, { OrderButtons } from "../../../../../components/button";
import { EditAddress } from "../../../../../user/settings/user-addresses/edit-address/edit-address";
import { MessageLoader } from "../../../../../../router/blocks/route-components/message.loader";
import {
  getCities,
  getCountries,
  getProvinces,
  getRegions,
} from "../../../../../../redux/slices/static-data.slice";
import {
  StandardDialog,
  StandardDialogFooter,
} from "../../../../../components/dialogs/standard-dialog/standard-dialog";
import { AdditionalServiceModel } from "../../../../../shared/models/order/additional-service.model";
import { ARMENIA_COUNTRY_ID } from "../../../../../../constants";

export const AddressDialog = ({
  order,
  open,
  isNewOrder,
  updateData,
  handleClose,
}: AdditionalServiceAddressDialogProps) => {
  const [isReady, setIsReady] = useState<boolean>();

  const [selectedUserAddress, setSelectedUserAddress] = useState<UserAddress>();
  const [selectedWarehouse, setSelectedWarehouse] = useState<WarehouseModel>();
  const [newAddressOpen, setNewAddressOpen] = useState(false);

  const { countries } = useAppSelector((state) => state.staticDataReducer);
  const { myAddressesData } = useAppSelector(
    (state) => state.userAddressesReducer,
  );
  const { additionalServices } = useAppSelector(
    (state) => state.staticDataReducer,
  );

  const armenia = countries.find(
    (country) => country.countryId === ARMENIA_COUNTRY_ID,
  );
  const destination =
    updateData && updateData.shippingInfo
      ? updateData.shippingInfo.homeDeliveryAddress ||
        updateData.shippingInfo.destinationWarehouse
      : order.shippingInfo.homeDeliveryAddress ||
        order.shippingInfo.destinationWarehouse;

  useEffect(() => {
    Promise.allSettled([
      dispatch(changePagination({ pageSize: 1000, currentPage: 0 })),
      dispatch(getMyAddresses({ forceLoad: true })),
    ]).then(() => {
      if (destination.hasOwnProperty("warehouseId")) {
        const warehouse = destination as WarehouseModel;
        setSelectedWarehouse(warehouse);
      } else {
        const userAddress = destination as UserAddress;
        setSelectedUserAddress(userAddress);
      }
      setIsReady(true);
    });
  }, []);

  const dispatch = useAppDispatch();

  function handleUserAddressSelect(userAddress: UserAddress) {
    setSelectedWarehouse(undefined);
    setSelectedUserAddress(userAddress);
  }

  function handleWarehouseSelect(warehouse: WarehouseModel) {
    setSelectedWarehouse(warehouse);
    setSelectedUserAddress(undefined);
  }

  function handleNewAddressClose(withChange: boolean) {
    if (withChange) {
      dispatch(getMyAddresses({ forceLoad: true })).then(
        (addressesResponse) => {
          const myAddresses: UserAddress[] =
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            addressesResponse.payload.myAddresses.addresses;

          const newAddress = myAddresses[myAddresses.length - 1];

          handleUserAddressSelect(newAddress);

          setNewAddressOpen(false);
        },
      );
    }
    setNewAddressOpen(false);
  }

  function handleSave() {
    const homeDeliveryService = additionalServices.find(
      (service) => service.additionalServiceId === 5,
    );
    const filteredOrderAdditionalServices =
      (
        (updateData && updateData.additionalService) ||
        order.additionalService
      ).filter(
        (service: AdditionalServiceModel) =>
          !(service.additionalServiceId === 5),
      ) || [];

    const newUpdateData = selectedUserAddress
      ? {
          shippingInfo: {
            ...order.shippingInfo,
            shippingInfoId: undefined,
            _id: undefined,
            homeDeliveryAddress: selectedUserAddress,
            destinationWarehouse: armenia?.warehouse[0],
          },
          additionalService: homeDeliveryService
            ? [homeDeliveryService, ...filteredOrderAdditionalServices]
            : filteredOrderAdditionalServices,
        }
      : {
          shippingInfo: {
            ...order.shippingInfo,
            shippingInfoId: undefined,
            _id: undefined,
            homeDeliveryAddress: null,
            destinationWarehouse: selectedWarehouse,
          },
          additionalService: filteredOrderAdditionalServices,
        };

    handleClose(true, newUpdateData);
  }

  const SingleAddress = ({
    icon,
    mainMessage,
    secondaryMessage,
    price,
    selected,
    onClick,
  }: SingleAddressProps) => {
    return (
      <SingleSelectItem
        Icon={
          <BarekamIcon
            variant={icon}
            size={{ width: "2rem", height: "2rem" }}
          />
        }
        TopLabel={
          <BarekamText variant={"m"} fontWeight={500}>
            {mainMessage}
          </BarekamText>
        }
        LowerLabel={
          <BarekamText variant={"s"} fontWeight={600} opaque>
            {secondaryMessage}
          </BarekamText>
        }
        RightLabel={
          <BarekamText variant={"m"} fontWeight={500}>
            {price}
          </BarekamText>
        }
        onClick={onClick}
        selected={selected}
      />
    );
  };

  function handleNewAddressClick() {
    Promise.allSettled([
      dispatch(getCountries()),
      dispatch(getProvinces()),
      dispatch(getCities()),
      dispatch(getRegions()),
    ]).then(() => {
      setNewAddressOpen(true);
    });
  }

  return (
    <StandardDialog
      headerLabel={
        <Message
          messageKey={"delivery_address"}
          sectionId={MessageSections.USER}
        />
      }
      open={open}
    >
      <DialogContent>
        {isReady ? (
          <>
            {armenia?.warehouse.map((warehouse) => {
              return (
                <SingleAddress
                  icon={OrderIcons.warehouseDelivery}
                  mainMessage={
                    <Message
                      messageKey={warehouse.city}
                      sectionId={MessageSections.MAIN}
                    />
                  }
                  secondaryMessage={<>{warehouse.addressLine1}</>}
                  price={
                    <Message
                      messageKey={"free"}
                      sectionId={MessageSections.USER}
                    />
                  }
                  selected={
                    !!selectedWarehouse &&
                    selectedWarehouse.warehouseId ===
                      order.shippingInfo.destinationWarehouse.warehouseId
                  }
                  onClick={() => handleWarehouseSelect(warehouse)}
                  key={warehouse.warehouseId}
                />
              );
            })}

            {myAddressesData.addresses.map((address) => {
              const addressString = [
                // address.country,
                // address.city,
                address?.province?.name,
                address?.region?.name,
                address.street,
                address.building,
                address.entrance,
                address.floor,
                address.apartment,
              ]
                .filter((value) => !!value)
                .join(", ");

              return (
                <SingleAddress
                  icon={OrderIcons.homeDelivery}
                  mainMessage={<>{address.name}</>}
                  secondaryMessage={<>{addressString}</>}
                  price={<>1000&nbsp;AMD</>}
                  selected={
                    !!selectedUserAddress &&
                    selectedUserAddress.addressId === address.addressId
                  }
                  onClick={() => handleUserAddressSelect(address)}
                  key={address.addressId}
                />
              );
            })}

            <BarekamButton
              type={OrderButtons.ADD_PRODUCT}
              onClick={() => handleNewAddressClick()}
            >
              <BarekamText variant={"xxl"} width={"unset"}>
                +
              </BarekamText>

              <BarekamText variant={"m"} bold>
                <Message
                  messageKey={"add_new_address"}
                  sectionId={MessageSections.USER}
                />
              </BarekamText>
            </BarekamButton>
          </>
        ) : (
          <></>
        )}
      </DialogContent>

      <MessageLoader sectionName={MessageSections.USER}>
        <StandardDialog
          headerLabel={
            <Message
              messageKey={"new_address"}
              sectionId={MessageSections.USER}
            />
          }
          open={newAddressOpen}
        >
          <EditAddress
            address={{} as UserAddress}
            onSave={handleNewAddressClose}
          />
        </StandardDialog>
      </MessageLoader>

      <StandardDialogFooter
        saveLabel={
          <Message messageKey={"ok"} sectionId={MessageSections.MAIN} />
        }
        onSave={() => handleSave()}
        onCancel={() => handleClose(false)}
      />
    </StandardDialog>
  );
};

interface SingleAddressProps {
  icon: OrderIcons;
  mainMessage: JSX.Element;
  secondaryMessage: JSX.Element;
  price: JSX.Element;
  selected: boolean;
  onClick: () => void;
}

interface AdditionalServiceAddressDialogProps {
  order: OrderModel;
  isNewOrder: boolean;
  open: boolean;
  updateData?: { [key: string]: any };
  handleClose: (withChange: boolean, existingOrderUpdates?: any) => void;
}
